/**
 * AskUserQuestionCard — UI for an `ask_user_question` interrupt (the agent
 * paused to ask the user a question via LangGraph's `interrupt()`).
 *
 * A dumb affordance, modeled on Claude Code's AskUserQuestion: render the
 * question + the agent's options as numbered rows (in order, with optional
 * icon + description), plus an optional free-text "Something else". Selecting
 * is deliberate — click a row (or press its number) to HIGHLIGHT it, then
 * "Send answer". Nothing is sent until then.
 *
 * Multi-select is the DEFAULT — the user is in charge of how many answers
 * apply: rows are checkboxes, the free-text answer rides ALONGSIDE the checked
 * options, and the resume is `{ indexes, text? }`. Only an explicit
 * `args.multiSelect: false` renders radios (picking one clears the rest,
 * resume `{ index }` or `{ text }`) — for truly exclusive choices.
 *
 * It collects an `AskUserQuestionResponse` and hands it back. On the backend
 * the tool reifies the exchange into durable transcript messages (aggregating
 * multi-select picks into one reply), so this card is transient.
 *
 * NOT part of the `InterruptCard` compound. The resume payload is built with
 * `buildAskUserQuestionResume` — the single source of truth the backend
 * round-trip tests also use.
 */
import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  BarChart3,
  Check,
  Clapperboard,
  Code2,
  Handshake,
  HeartPulse,
  HelpCircle,
  Home,
  Lightbulb,
  type LucideIcon,
  Megaphone,
  Package,
  PawPrint,
  Pencil,
  ShoppingBag,
  Shirt,
  Sparkles,
  UtensilsCrossed,
  Users,
} from "lucide-react";
import type { HumanInterrupt } from "../humanInterrupt";
import {
  buildAskUserQuestionResume,
  type AskUserQuestionArgs,
  type AskUserQuestionOption,
  type AskUserQuestionResponse,
} from "../humanInterrupt";

/** Agent-pickable semantic icon names → lucide glyphs. Unknown → Sparkles. */
const ICONS: Record<string, LucideIcon> = {
  product: Package,
  service: Handshake,
  software: Code2,
  content: Clapperboard,
  commerce: ShoppingBag,
  audience: Users,
  analytics: BarChart3,
  marketing: Megaphone,
  health: HeartPulse,
  home: Home,
  fashion: Shirt,
  pet: PawPrint,
  food: UtensilsCrossed,
  idea: Lightbulb,
};
const iconFor = (name?: string): LucideIcon => (name && ICONS[name]) || Sparkles;

/** Sentinel for "the free-text option is selected". */
const OTHER = "other" as const;
type SelectionKey = number | typeof OTHER;

export interface AskUserQuestionCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onSubmit"> {
  interrupt: HumanInterrupt;
  onResponse: (response: AskUserQuestionResponse) => Promise<void>;
  /** Label for the free-text option. Defaults to "Something else…". */
  otherLabel?: string;
  /** Placeholder for the free-text textarea. */
  otherPlaceholder?: string;
}

export function AskUserQuestionCard({
  interrupt,
  onResponse,
  otherLabel = "Something else…",
  otherPlaceholder = "Type your answer…",
  ...props
}: AskUserQuestionCardProps) {
  const args = interrupt.action_request.args as AskUserQuestionArgs;
  // Normalize: a bare string is shorthand for `{ label }`. Defends the card
  // against a producer/consumer version skew (objects vs strings) so it
  // renders instead of throwing React error #31.
  const options: AskUserQuestionOption[] = (args.options ?? []).map((o) =>
    typeof o === "string" ? { label: o } : o && typeof o === "object" ? o : { label: String(o) }
  );
  const allowOther = interrupt.config.allow_respond !== false;
  const multiSelect = args.multiSelect !== false;
  const totalRows = options.length + (allowOther ? 1 : 0);
  // Reserve the icon column for the whole list when any option carries an
  // icon, so labels stay aligned. The free-text row always shows its pencil.
  const showIcons = options.some((o) => o.icon) || allowOther;

  // One selection model for both modes: a set of row keys. Single-select keeps
  // the set at ≤1 entry (picking replaces); multi-select toggles membership.
  const [selected, setSelected] = useState<ReadonlySet<SelectionKey>>(new Set());
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = useCallback(
    (key: SelectionKey) => {
      setSelected((prev) => {
        if (!multiSelect) return new Set([key]);
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [multiSelect]
  );

  const pickedIndexes = [...selected].filter((k): k is number => typeof k === "number").sort((a, b) => a - b);
  const isOther = selected.has(OTHER);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOther) textareaRef.current?.focus();
  }, [isOther]);
  // Take focus when the card appears so the "1–N to pick" / Enter shortcuts
  // actually fire — otherwise the chat composer keeps focus and number keys
  // type into it instead of selecting a row. preventScroll keeps the
  // transcript from jumping.
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  // Something must be picked, and a checked "Something else" must have text —
  // in multi-select an empty free-text row blocks submit even when options are
  // checked (uncheck it or write the answer, don't silently drop it).
  const canSubmit =
    !submitting &&
    (pickedIndexes.length > 0 || isOther) &&
    (!isOther || otherText.trim().length > 0);

  const send = useCallback(
    async (response: AskUserQuestionResponse) => {
      setSubmitting(true);
      setError(null);
      try {
        await onResponse(response);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setSubmitting(false);
      }
    },
    [onResponse]
  );

  const submitSelection = useCallback(() => {
    if (submitting) return;
    const text = otherText.trim();
    if (multiSelect) {
      if (pickedIndexes.length === 0 && !(isOther && text)) return;
      void send(
        buildAskUserQuestionResume({
          indexes: pickedIndexes,
          ...(isOther && text ? { text } : {}),
        })
      );
    } else if (isOther && text.length > 0) {
      void send(buildAskUserQuestionResume({ text }));
    } else if (pickedIndexes.length > 0) {
      void send(buildAskUserQuestionResume({ index: pickedIndexes[0]! }));
    }
  }, [submitting, multiSelect, isOther, otherText, pickedIndexes, send]);

  const skip = useCallback(() => {
    if (submitting) return;
    void send(buildAskUserQuestionResume({ text: "(skipped)" }));
  }, [submitting, send]);

  // Keyboard: 1–N picks (or toggles, in multi-select) a row, Enter sends.
  // Ignored while typing free text.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT")) return;
      if (e.key === "Enter") {
        e.preventDefault();
        submitSelection();
        return;
      }
      const n = Number.parseInt(e.key, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= totalRows) {
        e.preventDefault();
        const idx = n - 1;
        if (idx < options.length) pick(idx);
        else if (allowOther) pick(OTHER);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [options.length, totalRows, allowOther, submitSelection, pick]);

  // Plain Enter submits from the free-text field too — answering should feel
  // like sending a chat message. Shift+Enter inserts a newline for the rare
  // multi-line answer (Cmd/Ctrl+Enter also submits, as muscle memory expects).
  const handleTextareaKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitSelection();
    }
  };

  return (
    <div
      {...props}
      ref={rootRef}
      role={multiSelect ? "group" : "radiogroup"}
      aria-label={args.question}
      tabIndex={-1}
      className={[
        "rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm focus:outline-none",
        "flex flex-col gap-4",
        props.className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-indigo-600">
          <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
          Question
        </div>
        <div className="text-lg font-semibold leading-snug text-zinc-900">{args.question}</div>
        {args.subtitle && <div className="text-sm text-zinc-500">{args.subtitle}</div>}
      </div>

      <div className="flex flex-col gap-2">
        {options.map((opt, index) => (
          <Row
            key={index}
            number={index + 1}
            label={opt.label}
            description={opt.description}
            Icon={showIcons ? iconFor(opt.icon) : undefined}
            active={selected.has(index)}
            multi={multiSelect}
            disabled={submitting}
            onClick={() => pick(index)}
          />
        ))}

        {allowOther && (
          <Row
            number={options.length + 1}
            label={otherLabel}
            Icon={showIcons ? Pencil : undefined}
            muted
            dashed
            active={isOther}
            multi={multiSelect}
            disabled={submitting}
            onClick={() => pick(OTHER)}
          />
        )}

        {isOther && (
          <textarea
            ref={textareaRef}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder={otherPlaceholder}
            disabled={submitting}
            className="mt-1 w-full min-h-[80px] rounded-xl border border-zinc-300 p-3 text-sm font-sans
                       focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
          />
        )}
      </div>

      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-1.5 text-xs text-zinc-400">
          <Kbd>1</Kbd>
          <span>–</span>
          <Kbd>{totalRows}</Kbd>
          <span>{multiSelect ? "to toggle" : "to pick"}</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            disabled={submitting}
            onClick={skip}
            className="text-sm font-medium text-zinc-500 hover:text-zinc-800 disabled:opacity-50"
          >
            Skip
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={submitSelection}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white
                       shadow-sm transition-colors hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Send answer"}
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1"
        >
          {error}
        </div>
      )}
    </div>
  );
}

interface RowProps {
  number: number;
  label: string;
  description?: string;
  Icon?: LucideIcon;
  active: boolean;
  /** Checkbox semantics + square indicator (multi-select) instead of a radio. */
  multi?: boolean;
  disabled?: boolean;
  muted?: boolean;
  dashed?: boolean;
  onClick: () => void;
}

function Row({ number, label, description, Icon, active, multi, disabled, muted, dashed, onClick }: RowProps) {
  return (
    <button
      type="button"
      role={multi ? "checkbox" : "radio"}
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={[
        "group w-full flex items-center gap-3 text-left px-3 py-3 rounded-xl border transition-all",
        active
          ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
          : dashed
            ? "border-dashed border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50"
            : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      ].join(" ")}
    >
      <span
        className={[
          "shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold",
          active ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-500",
        ].join(" ")}
      >
        {number}
      </span>

      {Icon && (
        <span
          className={[
            "shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border",
            active ? "border-indigo-200 bg-white text-indigo-600" : "border-zinc-200 bg-zinc-50 text-zinc-500",
          ].join(" ")}
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
      )}

      <span className="flex-1 min-w-0 flex flex-col">
        <span
          className={[
            "font-medium leading-tight",
            active ? "text-indigo-900" : muted ? "text-zinc-500" : "text-zinc-900",
          ].join(" ")}
        >
          {label}
        </span>
        {description && (
          <span className={["text-sm leading-tight", active ? "text-indigo-700/80" : "text-zinc-500"].join(" ")}>
            {description}
          </span>
        )}
      </span>

      {multi ? <Checkbox active={active} /> : <Radio active={active} />}
    </button>
  );
}

function Radio({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600">
        <span className="h-2 w-2 rounded-full bg-white" />
      </span>
    );
  }
  return <span className="shrink-0 h-5 w-5 rounded-full border-2 border-zinc-300 group-hover:border-zinc-400" />;
}

function Checkbox({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-md bg-indigo-600">
        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
      </span>
    );
  }
  return <span className="shrink-0 h-5 w-5 rounded-md border-2 border-zinc-300 group-hover:border-zinc-400" />;
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-zinc-300 px-1 text-[11px] font-medium text-zinc-500">
      {children}
    </span>
  );
}
